import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { Api } from '@brokerage/utils/Api';

// Thunk to fetch agencies
export const fetchAgencies = createAsyncThunk(
  'agencies/fetchAgencies',
  async (params, { getState, rejectWithValue }) => {
    try {
      const { page = 1, search = '', forceRefresh = false } = params || {};

      const cacheKey = search ? `${page}_${search}` : `${page}`;

      const { agenciesCache } = getState().agencies;

      if (agenciesCache[cacheKey] && !forceRefresh) {
        return {
          ...agenciesCache[cacheKey],
          cacheKey,
          currentPage: page,
          searchQuery: search
        };
      }

      const apiParams = { page, search };

      const { data, headers } = await Api.loadAgencies(apiParams);
      const pagination = JSON.parse(headers['x-pagination']);

      return {
        data,
        pagination,
        cacheKey,
        currentPage: page,
        searchQuery: search
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch agencies');
    }
  }
);

const initialState = {
  agenciesCache: {},
  agencies: [],
  pagination: {},
  loading: false,
  error: null,
  currentPage: 1,
  searchQuery: ''
};

const agenciesSlice = createSlice({
  name: 'agencies',
  initialState,
  reducers: {
    clearCache: state => {
      state.agenciesCache = {};
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchAgencies.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAgencies.fulfilled, (state, action) => {
        const { data, pagination, cacheKey, currentPage, searchQuery } = action.payload;

        state.agenciesCache[cacheKey] = { data, pagination };

        state.agencies = data;
        state.pagination = pagination;
        state.loading = false;
        state.error = null;
        state.currentPage = currentPage;
        state.searchQuery = searchQuery;
      })
      .addCase(fetchAgencies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch agencies';
      });
  }
});

export const { clearCache, setSearchQuery, setCurrentPage } = agenciesSlice.actions;
export default agenciesSlice.reducer;
